import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Package, Home, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { paymentService } from '../services/paymentService';

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('No payment session found');
      setIsLoading(false);
      return;
    }

    // Poll for payment status
    const checkStatus = async () => {
      try {
        const status = await paymentService.getCheckoutStatus(sessionId);
        setPaymentStatus(status);
        
        // If payment is successful, stop polling
        if (status.payment_status === 'paid') {
          setIsLoading(false);
        } else if (status.payment_status === 'failed' || status.payment_status === 'expired') {
          setError('Payment was not successful');
          setIsLoading(false);
        } else {
          // Continue polling if still pending
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setError('Failed to verify payment status');
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-12 pb-12 text-center">
            <Loader2 className="w-16 h-16 text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment...</h2>
            <p className="text-gray-400">Please wait while we confirm your payment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !paymentStatus) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Error</h2>
            <p className="text-gray-400 mb-6">{error || 'Something went wrong'}</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-white text-black hover:bg-amber-400 font-bold"
            >
              <Home className="w-4 h-4 mr-2" />
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="bg-zinc-900 border-zinc-800 max-w-2xl w-full">
        <CardContent className="pt-12 pb-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-lime-400/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-lime-400" />
            </div>
            <h1 className="text-4xl font-black text-white mb-2">Payment Successful!</h1>
            <p className="text-xl text-gray-300">Thank you for your purchase</p>
          </div>

          <div className="bg-zinc-800 rounded-lg p-6 mb-6">
            <h3 className="text-white font-bold mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2 text-amber-400" />
              Order Details
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Order ID:</span>
                <span className="text-white font-mono">{sessionId.slice(0, 20)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount Paid:</span>
                <span className="text-white font-bold">${paymentStatus.amount.toFixed(2)} {paymentStatus.currency.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Status:</span>
                <span className="text-lime-400 font-semibold">✓ Paid</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-4 mb-6">
            <h4 className="text-white font-semibold mb-2 text-sm">What happens next?</h4>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>✓ Order confirmation email sent to your email address</li>
              <li>✓ Your order will be processed within 24 hours</li>
              <li>✓ You'll receive tracking information once shipped</li>
              <li>✓ Estimated delivery: 3-5 business days</li>
            </ul>
          </div>

          <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-400 leading-relaxed">
              <strong className="text-red-400">No Refund Policy:</strong> All food and beverage products are non-refundable once purchased. Apparel items may be exchanged for size/color within 7 days of delivery. Please inspect your order upon delivery.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <Button
              onClick={() => navigate('/#products')}
              className="bg-amber-400 hover:bg-amber-500 text-black font-bold"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Continue Shopping
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
